import * as THREE from 'three';
import { mm }   from './units';
import { KEY_W } from './constants';


export interface KeyInfo {
  pos:     THREE.Vector3;
  midi:    number;
  isWhite: boolean;
  idx:     number;
}

export interface ParticleSystem {
  update(now: number,
         keys: KeyInfo[],
         hitCallback: (key: KeyInfo) => void): void;
  dispose(): void;
}

/**
 * Builds – and adds to the scene – a spiral “orbiter” particle system.
 * Returns an object that lets the caller drive it each frame and dispose it.
 */
export function createParticleSystem(
  scene: THREE.Scene,
  count: number,
): ParticleSystem {
  /* geometry / material ---------------------------------------------------- */
  const geo = new THREE.SphereGeometry(mm(10), 8, 8);
  const mat = new THREE.MeshBasicMaterial({ color: 'red' });
  const mesh = new THREE.InstancedMesh(geo, mat, count);
  scene.add(mesh);

  /* randomised motion parameters ------------------------------------------ */
  const axes:   THREE.Vector3[] = [];
  const bases:  THREE.Vector3[] = [];
  const speeds: number[]        = [];
  const phases: number[]        = [];

  for (let i = 0; i < count; i++) {
    const axis = new THREE.Vector3(
      Math.random() * 2 - 1,
      Math.random() * 2 - 1,
      Math.random() * 2 - 1,
    ).normalize();

    const tmp  = Math.abs(axis.x) < 0.9
      ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3(0, 1, 0);

    const base = tmp
      .sub(axis.clone().multiplyScalar(tmp.dot(axis)))
      .normalize();

    axes.push(axis);
    bases.push(base);
    speeds.push((Math.random() * 0.5 + 0.2) * (Math.random() < 0.5 ? -1 : 1));
    phases.push(Math.random() * Math.PI * 2);
  }

  /* per-frame update ------------------------------------------------------- */
  const tmpM   = new THREE.Matrix4();
  let   radius = 3;                           // updated from first key-centre

  function update(
    now: number,
    keys: KeyInfo[],
    hitCallback: (key: KeyInfo) => void,
  ) {
    if (keys.length) radius = keys[0].pos.length();   // keyboard radius

    for (let i = 0; i < count; i++) {
      const θ   = speeds[i] * now + phases[i];
      const pos = bases[i].clone().applyAxisAngle(axes[i], θ).multiplyScalar(radius);
      tmpM.makeTranslation(pos.x, pos.y, pos.z);
      mesh.setMatrixAt(i, tmpM);

      /* hit-test against every key centre ---------------------------------- */
      keys.forEach(k => {
        if (pos.distanceTo(k.pos) < KEY_W * 0.8) hitCallback(k);
      });
    }
    mesh.instanceMatrix.needsUpdate = true;
  }

  /* teardown --------------------------------------------------------------- */
  function dispose() {
    scene.remove(mesh);
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
  }

  return { update, dispose };
}
